import logger from '@/utils/logger';

import { AdminRoute } from '../../components/AdminRoute';
import { RestrictedBoardMemberRoute } from '../../components/RestrictedBoardMemberRoute';
import { ProtectedRoute } from '../guards';
import {
  ARInvoicesLandingPage,
  AccountPage,
  AssociationsPage,
  BoardPackagePage,
  BudgetPage,
  BudgetPlanningPage,
  CalendarPage,
  ComparisonDetailPage,
  ContractDetailPage,
  ContractsPage,
  CreateInvoicePage,
  CreateRfpPage,
  DashboardPage,
  DocumentDetailPage,
  DocumentsPage,
  ExpensesPage,
  IncomeCommitmentsPage,
  InvoiceDetailPage,
  InvoicesPage,
  LoansPage,
  NotificationPage,
  OwnershipsPage,
  PropertiesPage,
  ProposalDetailPage,
  RentRadarPage,
  RentRadarPropertiesPage,
  RfpDetailPage,
  UploadPage,
  UsersPage,
} from '../lazyPages';

export const mainLayoutChildren = [
  {
    path: 'dashboard',
    Component: () => (
      <ProtectedRoute>
        <DashboardPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Home route');
      return null;
    },
  },
  {
    path: 'ownerships',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <OwnershipsPage />
      </RestrictedBoardMemberRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Ownerships route');
      return null;
    },
  },

  {
    path: 'properties',
    Component: () => (
      <ProtectedRoute>
        <PropertiesPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Properties route');
      return null;
    },
  },
  {
    // The list that the nav's Rent Radar link opens when there are several rental properties.
    path: 'properties/rent-radar',
    Component: () => (
      <ProtectedRoute>
        <RentRadarPropertiesPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Rent radar properties route');
      return null;
    },
  },
  {
    // Registered after the static 'properties' path above, which react-router
    // ranks first only when both are declared.
    path: 'properties/:propertyId/rent-radar',
    Component: () => (
      <ProtectedRoute>
        <RentRadarPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Rent radar route');
      return null;
    },
  },
  {
    path: 'associations',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <AssociationsPage />
      </RestrictedBoardMemberRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Associations route');
      return null;
    },
  },
  // One route with an optional entity segment. Choosing an entity navigates
  // from /documents to /documents/:entityId; two separate entries gave the
  // page a new element on that navigation, which discarded local state such
  // as an open modal when the commit landed after a click.
  {
    path: 'documents/:entityId?',
    Component: () => (
      <ProtectedRoute>
        <DocumentsPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Documents route');
      return null;
    },
  },
  {
    path: 'document/:documentId',
    Component: () => (
      <ProtectedRoute>
        <DocumentDetailPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Document Detail route');
      return null;
    },
  },
  // Same shape as documents: one route, optional entity segment.
  {
    path: 'contracts/:entityId?',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <ContractsPage />
      </RestrictedBoardMemberRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Contracts route');
      return null;
    },
  },
  {
    path: 'contract/:contractId',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <ContractDetailPage />
      </RestrictedBoardMemberRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Contract Detail route');
      return null;
    },
  },
  {
    // Static segment must be registered alongside the dynamic sibling
    // below; react-router ranks 'rfp/create' above 'rfp/:rfpDisplayId'.
    path: 'rfp/create',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <CreateRfpPage />
      </RestrictedBoardMemberRoute>
    ),
  },
  {
    path: 'rfp/:rfpDisplayId',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <RfpDetailPage />
      </RestrictedBoardMemberRoute>
    ),
  },
  {
    path: 'proposal/:proposalDisplayId',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <ProposalDetailPage />
      </RestrictedBoardMemberRoute>
    ),
  },
  {
    path: 'comparison/:comparisonSessionDisplayId',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <ComparisonDetailPage />
      </RestrictedBoardMemberRoute>
    ),
  },
  {
    path: 'budgets',
    Component: () => (
      <ProtectedRoute>
        <BudgetPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'commitments/income',
    Component: () => (
      <ProtectedRoute>
        <IncomeCommitmentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'budget-planning',
    Component: () => (
      <ProtectedRoute>
        <BudgetPlanningPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'expenses',
    Component: () => (
      <ProtectedRoute>
        <ExpensesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'expenses/loans',
    Component: () => (
      <ProtectedRoute>
        <LoansPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'capital-management',
    Component: () => (
      <ProtectedRoute>
        <BoardPackagePage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Capital Management route');
      return null;
    },
  },
  {
    path: 'ar-invoices',
    Component: () => (
      <RestrictedBoardMemberRoute>
        <ARInvoicesLandingPage />
      </RestrictedBoardMemberRoute>
    ),
  },
  {
    path: 'ar-invoices/create',
    Component: () => (
      <ProtectedRoute>
        <CreateInvoicePage />
      </ProtectedRoute>
    ),
  },
  // Same shape as documents: one route, optional entity segment.
  {
    path: 'invoices/:entityId?',
    Component: () => (
      <ProtectedRoute>
        <InvoicesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'invoice/:invoiceId',
    Component: () => (
      <ProtectedRoute>
        <InvoiceDetailPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Invoice Detail route');
      return null;
    },
  },
  {
    path: 'calendar',
    Component: () => (
      <ProtectedRoute>
        <CalendarPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'account',
    Component: () => (
      <ProtectedRoute>
        <AccountPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Account route');
      return null;
    },
  },
  {
    path: 'upload',
    Component: () => (
      <ProtectedRoute>
        <UploadPage />
      </ProtectedRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Upload route');
      return null;
    },
  },
  {
    path: 'users',
    Component: () => (
      <AdminRoute>
        <UsersPage />
      </AdminRoute>
    ),
    loader: async () => {
      logger.log('[Loader] Users route');
      return null;
    },
  },
  {
    path: 'notifications',
    Component: () => (
      <ProtectedRoute>
        <NotificationPage />
      </ProtectedRoute>
    ),
  },
];
